import React, { useRef, useEffect, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { RoundedBox, Html } from "@react-three/drei";
import * as THREE from "three";
import { BRAND } from "../lib/brand";
import { useNight } from "../lib/worldTheme";
import { iconFor } from "../lib/icons";
import BuildingDetails from "./BuildingDetails";
import { FloraCluster } from "./Flora";

function WindowBands({ width, depth, height, color, night }) {
  const bands = [];
  const floors = Math.max(1, Math.floor(height / 2.4));
  for (let i = 1; i <= floors; i++) {
    const y = (i / (floors + 1)) * height;
    const litFront = night && (i * 7) % 4 !== 0;
    const litBack = night && (i * 7 + 2) % 4 !== 0;
    const litRight = night && (i * 5 + 1) % 4 !== 0;
    const litLeft = night && (i * 5 + 3) % 4 !== 0;
    const mat = (lit, emis = 1.05) => (
      <meshStandardMaterial color={lit ? BRAND.yellow : color} emissive={lit ? BRAND.yellow : "#000000"} emissiveIntensity={lit ? emis : 0} roughness={0.4} metalness={0.1} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
    );
    // front (+z) and back (-z)
    bands.push(
      <mesh key={`f${i}`} position={[0, y, depth / 2 + 0.06]}>
        <planeGeometry args={[width * 0.74, 0.32]} />{mat(litFront, 1.1)}
      </mesh>
    );
    bands.push(
      <mesh key={`b${i}`} position={[0, y, -(depth / 2 + 0.06)]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[width * 0.74, 0.32]} />{mat(litBack, 1.1)}
      </mesh>
    );
    // right (+x) and left (-x)
    bands.push(
      <mesh key={`r${i}`} position={[width / 2 + 0.06, y, 0]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[depth * 0.74, 0.32]} />{mat(litRight, 1.0)}
      </mesh>
    );
    bands.push(
      <mesh key={`l${i}`} position={[-(width / 2 + 0.06), y, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[depth * 0.74, 0.32]} />{mat(litLeft, 1.0)}
      </mesh>
    );
  }
  return <group>{bands}</group>;
}

// A glazed CURTAIN-WALL skin — dark glass + a light mullion grid + a field of LIT
// windows that gently twinkle and swap colour (light-blue / brand-yellow) on their
// own slow, random schedule — wrapped on ALL FOUR faces. Used for the Consulting
// data-tower so the facade reads as a living, lightly-animated office at any hour.
function CurtainWall({ w, d, height, baseY = 0, trim = "#EAE6DC", night, accent }) {
  const cols = 4;
  const rows = Math.max(2, Math.round(height / 1.3));
  const faces = [
    { rot: 0, pos: [0, 0, d / 2 + 0.05], len: w },
    { rot: Math.PI, pos: [0, 0, -(d / 2 + 0.05)], len: w },
    { rot: Math.PI / 2, pos: [w / 2 + 0.05, 0, 0], len: d },
    { rot: -Math.PI / 2, pos: [-(w / 2 + 0.05), 0, 0], len: d },
  ];
  // Light-blue / brand-yellow window palette (calm data-tower glow).
  const WIN = ["#7FD4E6", accent || BRAND.yellow];

  // Pre-compute the static glass + mullion frame per face, plus the set of LIT
  // windows that animate (kept stable across renders for steady per-cell timers).
  const { faceFrames, litCells } = useMemo(() => {
    const frames = [];
    const cells = [];
    faces.forEach((f, fi) => {
      const gw = f.len * 0.92;
      const gh = height * 0.9;
      const y0 = (height - gh) / 2;
      const cellW = gw / cols;
      const cellH = gh / rows;
      const lines = [];
      for (let r = 1; r < rows; r++) lines.push({ k: `h${r}`, pos: [0, y0 + (r / rows) * gh, 0.03], args: [gw, 0.05, 0.05] });
      for (let c = 1; c < cols; c++) lines.push({ k: `v${c}`, pos: [(c / cols - 0.5) * gw, height / 2, 0.03], args: [0.05, gh, 0.05] });
      frames.push({ fi, pos: f.pos, rot: f.rot, gw, gh, lines });
      // ~55% of the grid cells light up and animate; the rest stay dark glass.
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          if (Math.random() > 0.55) continue;
          cells.push({
            key: `${fi}-${c}-${r}`,
            pos: f.pos, rot: f.rot,
            x: (c / cols - 0.5) * gw + cellW / 2,
            y: y0 + (r / rows) * gh + cellH / 2,
            cw: cellW * 0.8, ch: cellH * 0.74,
            colorIdx: Math.random() < 0.5 ? 0 : 1,
            changeIn: Math.random() * 5,
            tw: 1.1 + Math.random() * 1.5,
            phase: Math.random() * Math.PI * 2,
            ref: { current: null },
          });
        }
      }
    });
    return { faceFrames: frames, litCells: cells };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, d, height]);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    const baseI = night ? 1.1 : 0.5;
    for (let k = 0; k < litCells.length; k++) {
      const c = litCells[k];
      const m = c.ref.current;
      if (!m) continue;
      c.changeIn -= dt;
      if (c.changeIn <= 0) {
        // mostly twinkle in place; sometimes swap blue<->yellow for gentle movement
        if (Math.random() < 0.5) c.colorIdx = c.colorIdx === 0 ? 1 : 0;
        m.material.color.set(WIN[c.colorIdx]);
        m.material.emissive.set(WIN[c.colorIdx]);
        c.changeIn = 2.2 + Math.random() * 4.5;
      }
      const twk = 0.6 + 0.4 * Math.sin(t * c.tw + c.phase);
      m.material.emissiveIntensity = baseI * twk;
    }
  });

  return (
    <group position={[0, baseY, 0]}>
      {faceFrames.map((f) => (
        <group key={f.fi} position={f.pos} rotation={[0, f.rot, 0]}>
          <mesh position={[0, height / 2, 0]}>
            <planeGeometry args={[f.gw, f.gh]} />
            <meshStandardMaterial color={"#1A2A38"} emissive={"#2E6486"} emissiveIntensity={night ? 0.5 : 0.12} roughness={0.12} metalness={0.6} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
          </mesh>
          {f.lines.map((ln) => (
            <mesh key={ln.k} position={ln.pos}><boxGeometry args={ln.args} /><meshStandardMaterial color={trim} roughness={0.5} /></mesh>
          ))}
        </group>
      ))}
      {litCells.map((c) => (
        <group key={c.key} position={c.pos} rotation={[0, c.rot, 0]}>
          <mesh ref={c.ref} position={[c.x, c.y, 0.045]}>
            <planeGeometry args={[c.cw, c.ch]} />
            <meshStandardMaterial
              color={WIN[c.colorIdx]}
              emissive={WIN[c.colorIdx]}
              emissiveIntensity={night ? 1.0 : 0.45}
              toneMapped={false}
              roughness={0.3}
              metalness={0.2}
              polygonOffset
              polygonOffsetFactor={-3}
              polygonOffsetUnits={-3}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// MARKETING facade — a vibrant multi-colour LED grid wrapping all four faces so the
// tower reads as a lit night-market screen-tower instead of a black slab.
function MarketingFacade({ w, d, height, palette, night, base = "#1A1D24" }) {
  const lines = Math.max(4, Math.floor(height / 1.5));
  const faces = [
    { rot: 0, pos: [0, 0, d / 2 + 0.04], len: w * 0.84 },
    { rot: Math.PI, pos: [0, 0, -(d / 2 + 0.04)], len: w * 0.84 },
    { rot: Math.PI / 2, pos: [w / 2 + 0.04, 0, 0], len: d * 0.84 },
    { rot: -Math.PI / 2, pos: [-(w / 2 + 0.04), 0, 0], len: d * 0.84 },
  ];
  // Build a flat list of LED "cells". Each cell changes colour ON ITS OWN slow,
  // random schedule (no synchronized scrolling chase) so the facade reads like a
  // calm billboard where individual windows light up in different colours.
  const cells = useMemo(() => {
    const arr = [];
    faces.forEach((f, fi) => {
      const segs = 4;
      const segLen = f.len / segs;
      for (let i = 0; i < lines; i++) {
        const y = ((i + 1) / (lines + 1)) * height;
        for (let sgi = 0; sgi < segs; sgi++) {
          const lx = (sgi - (segs - 1) / 2) * segLen;
          arr.push({
            key: `${fi}-${i}-${sgi}`,
            fpos: f.pos, frot: f.rot, lx, y, cw: segLen * 0.82,
            dead: (i * 3 + sgi * 7 + fi) % 6 === 0,     // ~17% permanently-dark pixels
            colorIdx: Math.floor(Math.random() * palette.length),
            changeIn: Math.random() * 4.5,              // staggered first colour change
            phase: Math.random() * Math.PI * 2,
            tw: 1.2 + Math.random() * 1.6,              // gentle twinkle speed
            ref: { current: null },
          });
        }
      }
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, d, height, lines]);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    const baseI = night ? 1.2 : 0.85;
    for (let k = 0; k < cells.length; k++) {
      const c = cells[k];
      const m = c.ref.current;
      if (!m || c.dead) continue;
      // Each window swaps to a NEW random colour only when its own slow timer ends.
      c.changeIn -= dt;
      if (c.changeIn <= 0) {
        let ni = Math.floor(Math.random() * palette.length);
        if (ni === c.colorIdx) ni = (ni + 1) % palette.length; // always visibly change
        c.colorIdx = ni;
        m.material.color.set(palette[ni]);
        m.material.emissive.set(palette[ni]);
        c.changeIn = 2.4 + Math.random() * 4.8;           // slow, random per-window cadence
      }
      const twk = 0.62 + 0.38 * Math.sin(t * c.tw + c.phase);
      m.material.emissiveIntensity = baseI * twk;
    }
  });

  return (
    <group>
      {cells.map((c) => (
        <group key={c.key} position={c.fpos} rotation={[0, c.frot, 0]}>
          <mesh ref={c.ref} position={[c.lx, c.y, 0]}>
            <planeGeometry args={[c.cw, 0.26]} />
            <meshStandardMaterial
              color={c.dead ? base : palette[c.colorIdx % palette.length]}
              emissive={c.dead ? "#000000" : palette[c.colorIdx % palette.length]}
              emissiveIntensity={c.dead ? 0 : night ? 1.1 : 0.8}
              toneMapped={false}
              roughness={0.4}
              polygonOffset
              polygonOffsetFactor={-2}
              polygonOffsetUnits={-2}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// BRANDING — the two top offset cubes each drift on their own vertical axis with
// an INDEPENDENT, random slow sway: every few seconds each cube eases toward a new
// random small angle, on its own schedule, so the two never move in sync.
function BrandingTops({ w, d, s, color, accent }) {
  const b2 = useRef();
  const b3 = useRef();
  const sway = useRef([
    // MIDDLE cube (b2): rotates MORE but SLOWER, around its own centred axis
    { base: 0.20, cur: 0, tgt: 0, t: 0, dur: 6 + Math.random() * 3.5, amp: 0.36 + Math.random() * 0.1, ease: 0.22 + Math.random() * 0.12 },
    // TOP cube (b3): subtle and on its own independent schedule
    { base: -0.30, cur: 0, tgt: 0, t: 0, dur: 3.5 + Math.random() * 2.5, amp: 0.28 + Math.random() * 0.1, ease: 0.5 + Math.random() * 0.4 },
  ]);
  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    const refs = [b2.current, b3.current];
    for (let i = 0; i < 2; i++) {
      const sw = sway.current[i];
      sw.t += dt;
      if (sw.t >= sw.dur) {
        sw.t = 0;
        sw.dur = (i === 0 ? 6 : 3.5) + Math.random() * 3.5;
        sw.tgt = (Math.random() * 2 - 1) * sw.amp; // new independent random target
      }
      sw.cur += (sw.tgt - sw.cur) * Math.min(1, dt * sw.ease);
      if (refs[i]) refs[i].rotation.y = sw.base + sw.cur;
    }
  });

  // Clean, perfectly CENTRED, FLUSH vertical stack. The base box (rendered by the
  // caller) is anchored at y=0 with height s*1.15, so its ceiling is exactly s*1.15.
  // Each block sits flush on the ceiling of the one below — no gaps, no clipping.
  // All blocks share the same x/z centre, so their independent Y-axis sway can never
  // make them intersect.
  const baseTop = s * 1.15;
  const h2 = s * 1.0;                  // middle block height
  const h3 = s * 0.85;                 // top block height
  const y2 = baseTop + h2 / 2;         // centred on the base ceiling
  const y3 = baseTop + h2 + h3 / 2;    // centred on the middle ceiling
  const w2 = w * 0.82, d2 = d * 0.82;  // middle block footprint (tapered in)
  const w3 = w * 0.64, d3 = d * 0.64;  // top block footprint (tapered further)

  return (
    <group>
      <group ref={b2} position={[0, y2, 0]}>
        <RoundedBox args={[w2, h2, d2]} radius={0.12} smoothness={3} castShadow receiveShadow>
          <meshStandardMaterial color={color} roughness={0.55} />
        </RoundedBox>
      </group>
      <group ref={b3} position={[0, y3, 0]}>
        <RoundedBox args={[w3, h3, d3]} radius={0.1} smoothness={3} castShadow receiveShadow>
          <meshStandardMaterial color={color} roughness={0.55} />
        </RoundedBox>
        {/* brand mark centred on the top block's front face — sways gently with it */}
        <mesh position={[0, 0, d3 / 2 + 0.04]}>
          <boxGeometry args={[w3 * 0.6, h3 * 0.52, 0.1]} />
          <meshStandardMaterial color={accent} roughness={0.35} metalness={0.2} />
        </mesh>
      </group>
    </group>
  );
}

// EXPERIENTIAL CROWN — a modern sculptural diadem that replaces the old dome:
// a ring of upward-swept charcoal blades with glowing yellow leading edges, all
// crowned by a slowly-spinning faceted gem. Reads as a contemporary art-piece roof.
function ExperientialCrown({ R, baseY }) {
  const gemRef = useRef();
  const ringRef = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (gemRef.current) {
      gemRef.current.rotation.y = t * 0.55;
      gemRef.current.material.emissiveIntensity = 1.0 + (Math.sin(t * 2) * 0.5 + 0.5) * 0.8;
    }
    if (ringRef.current) ringRef.current.rotation.y = -t * 0.22;
  });
  const blades = useMemo(() => {
    const arr = [];
    const N = 16;
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      const tall = i % 2 === 0;
      const bh = tall ? R * 1.05 : R * 0.66;
      arr.push({ a, bh, tall, i });
    }
    return arr;
  }, [R]);
  const rr = R * 0.82;
  return (
    <group position={[0, baseY, 0]}>
      {/* charcoal cap that closes the open top of the video drum */}
      <mesh position={[0, 0.16, 0]} castShadow>
        <cylinderGeometry args={[R * 0.99, R + 0.07, 0.42, 64]} />
        <meshStandardMaterial color={BRAND.charcoal} roughness={0.5} metalness={0.25} />
      </mesh>
      {/* sculptural crown of swept blades (slowly rotating) */}
      <group ref={ringRef}>
        {blades.map(({ a, bh, tall, i }) => {
          const x = Math.cos(a) * rr;
          const z = Math.sin(a) * rr;
          return (
            <group key={i} position={[x, 0.34, z]} rotation={[0, -a, 0]}>
              {/* lean the top of each blade inward toward the centre */}
              <group rotation={[0, 0, 0.22]}>
                <mesh position={[0, bh / 2, 0]} castShadow>
                  <boxGeometry args={[0.14, bh, 0.5]} />
                  <meshStandardMaterial color={BRAND.charcoal} roughness={0.45} metalness={0.3} />
                </mesh>
                {/* glowing yellow leading edge */}
                <mesh position={[0, bh / 2, 0.26]}>
                  <boxGeometry args={[0.07, bh * 0.96, 0.06]} />
                  <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={1.1} toneMapped={false} />
                </mesh>
                {/* glowing tip cap */}
                <mesh position={[0, bh + 0.05, 0.1]}>
                  <boxGeometry args={[0.17, 0.17, 0.5]} />
                  <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={tall ? 1.4 : 0.85} toneMapped={false} />
                </mesh>
              </group>
            </group>
          );
        })}
      </group>
      {/* halo ring suspended under the gem */}
      <mesh position={[0, R * 0.55, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[R * 0.5, 0.05, 8, 40]} />
        <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={0.9} toneMapped={false} />
      </mesh>
      {/* slowly spinning faceted apex gem */}
      <mesh ref={gemRef} position={[0, R * 0.85, 0]} castShadow>
        <octahedronGeometry args={[R * 0.3, 0]} />
        <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={1.2} metalness={0.3} roughness={0.2} toneMapped={false} />
      </mesh>
      {/* warm glow cast by the gem */}
      <pointLight position={[0, R * 0.7, 0]} color={"#FFE9A8"} intensity={1.1} distance={R * 6} decay={2} />
    </group>
  );
}

// EXPERIENTIAL — the building's whole curved facade IS a seamless LED video wall.
// The brand reel is mapped directly onto a smooth cylindrical body (no separate
// floating panel) using a plain HTMLVideoElement + THREE.VideoTexture so it never
// blocks rendering.
function ExperientialBody({ w, h, color, accent }) {
  const texture = useMemo(() => {
    const video = document.createElement("video");
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    video.setAttribute("playsinline", "");
    video.setAttribute("muted", "");
    const srcWebm = document.createElement("source");
    srcWebm.src = "/experiential.webm?v=2";
    srcWebm.type = "video/webm";
    const srcMp4 = document.createElement("source");
    srcMp4.src = "/experiential.mp4?v=2";
    srcMp4.type = "video/mp4";
    video.appendChild(srcWebm);
    video.appendChild(srcMp4);
    video.load();
    const tex = new THREE.VideoTexture(video);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.repeat.set(1, 1);     // stretch the clip once around the facade
    tex.offset.x = 0.62;      // rotate the wrap seam toward the back
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = 8;
    tex.userData.video = video;
    return tex;
  }, []);

  useEffect(() => {
    const video = texture.userData.video;
    const tryPlay = () => { const p = video.play(); if (p && p.catch) p.catch(() => {}); };
    tryPlay();
    video.addEventListener("canplay", tryPlay);
    video.addEventListener("loadeddata", tryPlay);
    return () => {
      video.removeEventListener("canplay", tryPlay);
      video.removeEventListener("loadeddata", tryPlay);
      video.pause();
      texture.dispose();
    };
  }, [texture]);

  useFrame(() => {
    const video = texture.userData.video;
    if (!video) return;
    if (video.paused && video.readyState >= 2) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
    if (video.readyState >= 2) texture.needsUpdate = true;
  });

  const R = w * 0.72;
  const bodyH = h * 0.92;
  return (
    <group>
      {/* base plinth */}
      <mesh position={[0, 0.18, 0]} receiveShadow castShadow>
        <cylinderGeometry args={[R + 0.18, R + 0.3, 0.36, 64]} />
        <meshStandardMaterial color={BRAND.charcoal} roughness={0.6} metalness={0.15} />
      </mesh>
      {/* the LED video facade — the body itself */}
      <mesh position={[0, bodyH / 2 + 0.34, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[R, R, bodyH, 64, 1, true]} />
        <meshStandardMaterial map={texture} emissiveMap={texture} emissive={"#ffffff"} emissiveIntensity={0.95} toneMapped={false} roughness={0.4} metalness={0.05} side={THREE.FrontSide} />
      </mesh>
      {/* modern sculptural crown replaces the old dome */}
      <ExperientialCrown R={R} baseY={bodyH + 0.34} />
    </group>
  );
}

function ShapeMesh({ b, night }) {
  const w = Number(b.width) || 4;
  const d = Number(b.depth) || 4;
  const h = Number(b.height) || 6;
  const color = b.color || BRAND.charcoal;
  const accent = b.accent || BRAND.yellow;
  const windowColor = (color || "").toUpperCase() === "#FFCE24" ? BRAND.charcoal : "#8FB4C8";

  // CONTACT / THE BEACON — a modern twisting SIGNAL tower: a faceted charcoal
  // monolith that spirals upward, wrapped by a glowing yellow light-ribbon and
  // crowned by a luminous lantern-lens that broadcasts the "send a signal" pulse
  // (the live glow + signal rings are added by AnimatedBeacon in BuildingDetails).
  if (b.shape === "beacon") {
    const towerH = h * 0.66;
    const segs = 9;
    const segH = towerH / segs;
    const baseW = w * 1.02;
    const topW = w * 0.5;
    const twist = Math.PI * 0.34;
    const crownY = towerH;
    const lensH = w * 1.2;
    const lightY = crownY + 0.3 + lensH / 2;
    const segMeshes = [];
    for (let i = 0; i < segs; i++) {
      const segW = THREE.MathUtils.lerp(baseW, topW, i / segs);
      const rotY = twist * (i / (segs - 1));
      const y = i * segH + segH / 2;
      segMeshes.push(
        <group key={i} position={[0, y, 0]} rotation={[0, rotY, 0]}>
          <RoundedBox args={[segW, segH * 1.06, segW]} radius={0.1} smoothness={3} castShadow receiveShadow>
            <meshStandardMaterial color={color} roughness={0.42} metalness={0.32} />
          </RoundedBox>
          {/* glowing light-ribbon on the front facet — twists up with the tower */}
          <mesh position={[0, 0, segW / 2 + 0.02]}>
            <boxGeometry args={[segW * 0.16, segH * 1.04, 0.06]} />
            <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 1.3 : 0.95} toneMapped={false} />
          </mesh>
        </group>
      );
    }
    const mullions = [0, 1, 2, 3, 4, 5];
    return (
      <group>
        {/* low hex base — kept flush so the entrance reads at GROUND level (people
            walk straight in off the path instead of appearing to climb a plinth) */}
        <mesh position={[0, 0.09, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[baseW * 1.0, baseW * 1.08, 0.18, 6]} />
          <meshStandardMaterial color={color} roughness={0.6} metalness={0.2} />
        </mesh>
        <mesh position={[0, 0.21, 0]}>
          <cylinderGeometry args={[baseW * 0.86, baseW * 0.92, 0.08, 6]} />
          <meshStandardMaterial color={BRAND.charcoalLight} roughness={0.5} metalness={0.25} />
        </mesh>
        {/* twisting faceted shaft */}
        {segMeshes}
        {/* recessed dark doorway + glowing surround at GROUND level on the front
            face, lined up with the walk-in door so it reads as a real entrance */}
        <mesh position={[0, 1.0, baseW * 0.53]}>
          <planeGeometry args={[w * 0.46, 2.0]} />
          <meshStandardMaterial color={"#0E1116"} roughness={0.7} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
        </mesh>
        <mesh position={[0, 1.0, baseW * 0.54]}>
          <planeGeometry args={[w * 0.32, 1.86]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 0.95 : 0.45} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
        </mesh>
        {/* crown collar */}
        <mesh position={[0, crownY + 0.15, 0]} castShadow>
          <cylinderGeometry args={[topW * 0.78, topW * 0.92, 0.5, 6]} />
          <meshStandardMaterial color={color} roughness={0.45} metalness={0.3} />
        </mesh>
        {/* luminous lantern lens core */}
        <mesh position={[0, lightY, 0]}>
          <cylinderGeometry args={[topW * 0.42, topW * 0.42, lensH, 24]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 1.5 : 1.05} toneMapped={false} roughness={0.25} metalness={0.1} />
        </mesh>
        {/* modern lantern cage — slim charcoal mullions around the lens */}
        {mullions.map((i) => {
          const a = (i / mullions.length) * Math.PI * 2;
          return (
            <mesh key={`mu${i}`} position={[Math.cos(a) * topW * 0.5, lightY, Math.sin(a) * topW * 0.5]}>
              <boxGeometry args={[0.08, lensH * 1.04, 0.08]} />
              <meshStandardMaterial color={color} roughness={0.4} metalness={0.35} />
            </mesh>
          );
        })}
        {/* ceiling ring above the lens */}
        <mesh position={[0, lightY + lensH / 2 + 0.18, 0]} castShadow>
          <cylinderGeometry args={[topW * 0.62, topW * 0.5, 0.36, 6]} />
          <meshStandardMaterial color={color} roughness={0.45} metalness={0.3} />
        </mesh>
        {/* sleek faceted cap */}
        <mesh position={[0, lightY + lensH / 2 + 0.5 + topW * 0.45, 0]} castShadow>
          <coneGeometry args={[topW * 0.58, topW * 0.9, 6]} />
          <meshStandardMaterial color={color} roughness={0.4} metalness={0.35} />
        </mesh>
      </group>
    );
  }

  // THE DRILL DOWN — clean white kiosk (logo lives on the walls, see BuildingDetails)
  if (b.shape === "kiosk") {
    return (
      <RoundedBox args={[w, h, d]} radius={0.12} smoothness={3} position={[0, h / 2, 0]} castShadow receiveShadow>
        <meshStandardMaterial color={color} roughness={0.6} metalness={0.05} />
      </RoundedBox>
    );
  }

  // THE HQ — flagship slate tower with gold fins, lit glass crown + spire
  if (b.shape === "hq") {
    const bodyColor = "#363B47"; // slate (less black) so it reads with colour
    return (
      <group>
        <RoundedBox args={[w, h, d]} radius={0.14} smoothness={4} position={[0, h / 2, 0]} castShadow receiveShadow>
          <meshStandardMaterial color={bodyColor} roughness={0.5} metalness={0.12} />
        </RoundedBox>
        <WindowBands width={w} depth={d} height={h} color={"#8FB4C8"} night={night} />
        {/* gold vertical accent fins on ALL four corners */}
        {[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([sx, sz], i) => (
          <mesh key={i} position={[sx * (w / 2 - 0.05), h * 0.5, sz * (d / 2 - 0.05)]}>
            <boxGeometry args={[0.34, h, 0.34]} />
            <meshStandardMaterial color={accent} roughness={0.35} metalness={0.3} emissive={accent} emissiveIntensity={night ? 0.5 : 0.12} />
          </mesh>
        ))}
        {/* blue-glass entrance atrium at the base */}
        <mesh position={[0, h * 0.17, d / 2 + 0.05]}>
          <planeGeometry args={[w * 0.62, h * 0.34]} />
          <meshStandardMaterial color={"#6FA8C7"} transparent opacity={0.9} roughness={0.15} metalness={0.4} emissive={"#6FA8C7"} emissiveIntensity={night ? 0.45 : 0.06} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
        </mesh>
        {/* matching glazed lobby strip on the BACK so it isn't bare */}
        <mesh position={[0, h * 0.17, -(d / 2 + 0.05)]} rotation={[0, Math.PI, 0]}>
          <planeGeometry args={[w * 0.62, h * 0.34]} />
          <meshStandardMaterial color={"#6FA8C7"} transparent opacity={0.9} roughness={0.15} metalness={0.4} emissive={"#6FA8C7"} emissiveIntensity={night ? 0.4 : 0.06} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
        </mesh>
        {/* gold crown band */}
        <RoundedBox args={[w * 0.92, h * 0.12, d * 0.92]} radius={0.06} smoothness={3} position={[0, h + 0.1, 0]} castShadow>
          <meshStandardMaterial color={accent} roughness={0.4} metalness={0.3} emissive={accent} emissiveIntensity={night ? 0.4 : 0.08} />
        </RoundedBox>
        {/* lit glass crown box */}
        <mesh position={[0, h + 0.6, 0]}>
          <boxGeometry args={[w * 0.72, 0.8, d * 0.72]} />
          <meshStandardMaterial color={"#7FB9CE"} transparent opacity={0.85} roughness={0.2} metalness={0.3} emissive={"#7FB9CE"} emissiveIntensity={night ? 0.7 : 0.18} />
        </mesh>
        {/* spire */}
        <mesh position={[0, h + 1.7, 0]} castShadow>
          <cylinderGeometry args={[0.04, 0.16, 1.8, 8]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 1.1 : 0.3} metalness={0.4} roughness={0.3} />
        </mesh>
      </group>
    );
  }

  // BRANDING — sculptural stack of rotated, offset blocks
  if (b.slug === "branding") {
    const s = h / 3;
    return (
      <group>
        <RoundedBox args={[w, s * 1.15, d]} radius={0.12} smoothness={3} position={[0, s * 0.575, 0]} castShadow receiveShadow>
          <meshStandardMaterial color={color} roughness={0.55} />
        </RoundedBox>
        <WindowBands width={w} depth={d} height={s * 1.15} color={windowColor} night={night} />
        <BrandingTops w={w} d={d} s={s} color={color} accent={accent} />
      </group>
    );
  }

  // MARKETING — an ABSTRACT, multi-colour "night-market" tower: a teal base, a
  // deep-indigo LED screen-tower (multi-colour grid + neon pinstripes), and an
  // offset cluster of colourful crown volumes + a cantilevered yellow blade.
  if (b.slug === "marketing") {
    const palette = ["#FF2E97", "#19E3E8", BRAND.yellow, "#9B5CFF", "#FF7A1A"];
    const core = "#34416E"; // deep indigo-blue body — clearly NOT black
    const baseCol = "#1C5A66"; // teal base plinth (a different colour, as requested)
    const plinthH = 2.2;
    const towerY = plinthH;
    const towerH = h * 0.78;
    const topY = towerY + towerH;
    const hw = w / 2 - 0.05, hd = d / 2 - 0.05;
    const corners = [[hw, hd, palette[0]], [-hw, hd, palette[1]], [hw, -hd, palette[2]], [-hw, -hd, palette[3]]];
    const crown = [
      { args: [w * 0.52, 1.8, d * 0.52], pos: [-w * 0.2, topY + 0.9, w * 0.16], col: "#7A1E55", emis: "#FF2E97" },
      { args: [w * 0.4, 1.25, d * 0.4], pos: [w * 0.26, topY + 0.6, -w * 0.16], col: "#15565E", emis: "#19E3E8" },
    ];
    return (
      <group>
        {/* teal base plinth (different colour, wider — grounds the tower) */}
        <RoundedBox args={[w + 0.9, plinthH, d + 0.9]} radius={0.16} smoothness={3} position={[0, plinthH / 2, 0]} castShadow receiveShadow>
          <meshStandardMaterial color={baseCol} roughness={0.55} metalness={0.15} />
        </RoundedBox>
        {/* glowing yellow ledge band between plinth and tower */}
        <mesh position={[0, plinthH + 0.02, 0]}>
          <boxGeometry args={[w + 0.96, 0.14, d + 0.96]} />
          <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 0.8 : 0.32} toneMapped={false} roughness={0.4} />
        </mesh>
        {/* main colourful "screen" tower */}
        <RoundedBox args={[w, towerH, d]} radius={0.12} smoothness={3} position={[0, towerY + towerH / 2, 0]} castShadow receiveShadow>
          <meshStandardMaterial color={core} roughness={0.5} metalness={0.2} />
        </RoundedBox>
        {/* multi-colour LED facade over the tower (unlit cells blend into the body) */}
        <group position={[0, towerY, 0]}>
          <MarketingFacade w={w} d={d} height={towerH} palette={palette} night={night} base={core} />
        </group>
        {/* glowing neon corner pinstripes on the tower */}
        {corners.map(([cx, cz, col], i) => (
          <mesh key={i} position={[cx, towerY + towerH * 0.5, cz]}>
            <boxGeometry args={[0.1, towerH * 0.98, 0.1]} />
            <meshStandardMaterial color={col} emissive={col} emissiveIntensity={1.2} toneMapped={false} />
          </mesh>
        ))}
        {/* abstract offset colourful crown volumes (varied size + palette) */}
        {crown.map((c, i) => (
          <group key={i}>
            <RoundedBox args={c.args} radius={0.08} smoothness={3} position={c.pos} castShadow>
              <meshStandardMaterial color={c.col} roughness={0.5} metalness={0.2} />
            </RoundedBox>
            <mesh position={[c.pos[0], c.pos[1] + c.args[1] / 2 + 0.04, c.pos[2]]}>
              <boxGeometry args={[c.args[0] * 1.02, 0.1, c.args[2] * 1.02]} />
              <meshStandardMaterial color={c.emis} emissive={c.emis} emissiveIntensity={1.3} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* cantilevered yellow blade for an abstract silhouette */}
        <mesh position={[w * 0.42, topY + 1.0, 0]} rotation={[0, 0, 0.16]} castShadow>
          <boxGeometry args={[0.28, 2.4, d * 0.5]} />
          <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 0.7 : 0.18} roughness={0.4} metalness={0.2} />
        </mesh>
        {/* antenna + warm tip */}
        <mesh position={[-w * 0.22, topY + 1.5, w * 0.16]}>
          <cylinderGeometry args={[0.04, 0.06, 2.2, 6]} />
          <meshStandardMaterial color={BRAND.charcoal} metalness={0.4} roughness={0.4} />
        </mesh>
        <mesh position={[-w * 0.22, topY + 2.65, w * 0.16]}>
          <sphereGeometry args={[0.12, 10, 10]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 1.2 : 0.6} toneMapped={false} />
        </mesh>
      </group>
    );
  }

  // CONSULTING (Strategy & Data) — re-imagined as a modern DARK-GLASS terraced
  // data-tower: stepped glazed volumes wrapped in curtain-wall on every face, lit
  // data panels, a glass lobby + canopy, and rooftop data viz (in BuildingDetails).
  if (b.slug === "consulting") {
    const glass = "#262B34";
    const trim = "#EDE9DF";
    const v1 = { w: w * 1.4, h: h * 0.45, d: d * 1.15, x: 0, z: 0, y: 0 };
    const v2 = { w: w * 1.05, h: h * 0.4, d: d * 0.85, x: 0, z: -d * 0.12, y: v1.h };
    const v3 = { w: w * 0.66, h: h * 0.34, d: d * 0.58, x: w * 0.2, z: -d * 0.04, y: v1.h + v2.h };
    const vols = [v1, v2, v3];
    return (
      <group>
        {vols.map((v, i) => (
          <group key={i} position={[v.x, 0, v.z]}>
            <RoundedBox args={[v.w, v.h, v.d]} radius={0.08} smoothness={3} position={[0, v.y + v.h / 2, 0]} castShadow receiveShadow>
              <meshStandardMaterial color={glass} roughness={0.3} metalness={0.4} />
            </RoundedBox>
            <CurtainWall w={v.w} d={v.d} height={v.h} baseY={v.y} trim={trim} night={night} accent={accent} />
            {/* yellow trim cap on each terrace */}
            <mesh position={[0, v.y + v.h + 0.03, 0]}>
              <boxGeometry args={[v.w * 1.015, 0.12, v.d * 1.015]} />
              <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={night ? 0.6 : 0.18} roughness={0.4} metalness={0.2} />
            </mesh>
          </group>
        ))}
        {/* glass lobby + cantilevered canopy at the front of the base volume */}
        <group position={[0, 0, v1.d / 2]}>
          <mesh position={[0, h * 0.16, 0.06]}>
            <planeGeometry args={[w * 0.5, h * 0.3]} />
            <meshStandardMaterial color={"#8FD0E6"} emissive={"#8FD0E6"} emissiveIntensity={night ? 0.6 : 0.12} roughness={0.1} metalness={0.4} polygonOffset polygonOffsetFactor={-3} polygonOffsetUnits={-3} />
          </mesh>
          <mesh position={[0, h * 0.33, 0.55]} rotation={[0.3, 0, 0]} castShadow>
            <boxGeometry args={[w * 0.66, 0.12, 1.1]} />
            <meshStandardMaterial color={accent} roughness={0.4} metalness={0.2} />
          </mesh>
          {[-w * 0.28, w * 0.28].map((x) => (
            <mesh key={x} position={[x, h * 0.08, 0.95]}>
              <cylinderGeometry args={[0.05, 0.05, h * 0.32, 8]} />
              <meshStandardMaterial color={trim} metalness={0.3} roughness={0.4} />
            </mesh>
          ))}
        </group>
      </group>
    );
  }

  // EXPERIENTIAL — rotunda whose facade is a seamless LED video wall
  if (b.slug === "experiential") {
    return (
      <group>
        <ExperientialBody w={w} h={h} color={color} accent={accent} />
      </group>
    );
  }

  // default tower (fallback)
  const radius = b.shape === "block" ? 0.1 : 0.14;
  return (
    <group>
      <RoundedBox args={[w, h, d]} radius={radius} smoothness={4} position={[0, h / 2, 0]} castShadow receiveShadow>
        <meshStandardMaterial color={color} roughness={0.58} metalness={0.06} />
      </RoundedBox>
      <WindowBands width={w} depth={d} height={h} color={windowColor} night={night} />
      <RoundedBox args={[w * 0.62, 0.6, d * 0.62]} radius={0.06} smoothness={2} position={[0, h + 0.3, 0]} castShadow>
        <meshStandardMaterial color={accent} roughness={0.4} />
      </RoundedBox>
    </group>
  );
}

export default function Building({ b, isActive, isHovered, dimmed, onSelect, onHover, showLabels = true }) {
  const groupRef = useRef();
  const visualRef = useRef();
  const ringRef = useRef();
  const hoverLightRef = useRef();
  const markerRef = useRef();
  const amt = useRef(0);
  const { invalidate } = useThree();
  const night = useNight();
  const Icon = iconFor(b.slug);

  const w = Number(b.width) || 4;
  const d = Number(b.depth) || 4;
  const h = Number(b.height) || 6;
  const markerBaseY = h + (b.shape === "beacon" ? 5.6 : 3.4);
  const floraSeed = useMemo(() => {
    let s = 0;
    const str = String(b.slug || b.id || "x");
    for (let i = 0; i < str.length; i++) s = (s * 31 + str.charCodeAt(i)) >>> 0;
    return s || 7;
  }, [b.slug, b.id]);

  const target = isActive || isHovered ? 1 : 0;

  useEffect(() => {
    invalidate();
  }, [isActive, isHovered, dimmed, invalidate]);

  // Make the animated building geometry NON-interactive so pointer events come
  // only from the static hit-proxy below. This stops the hover from flickering:
  // as the building lifts/scales on hover, the moving meshes would otherwise slip
  // out from under the cursor and fire rapid onPointerOut/onPointerOver events.
  useEffect(() => {
    const root = visualRef.current;
    if (!root) return;
    root.traverse((o) => {
      o.raycast = () => null;
    });
  }, [b.slug, b.shape, night]);

  useFrame((state, delta) => {
    const e = 1 - Math.pow(0.0009, Math.min(delta, 0.05));
    amt.current = THREE.MathUtils.lerp(amt.current, target, e);
    const a = amt.current;
    const time = state.clock.elapsedTime;
    if (ringRef.current) {
      ringRef.current.material.opacity = a * (isActive ? 0.92 : 0.58);
      const pulse = 1 + Math.sin(time * 3) * 0.03;
      ringRef.current.scale.set(pulse, pulse, 1);
    }
    if (hoverLightRef.current) hoverLightRef.current.intensity = a * 2.6;
    if (markerRef.current) {
      markerRef.current.visible = a > 0.02;
      markerRef.current.scale.setScalar(a);
      markerRef.current.position.y = markerBaseY + Math.sin(time * 2.6) * 0.16;
      markerRef.current.rotation.y = time * 1.4;
    }
  });

  return (
    <group position={[Number(b.positionX) || 0, 0, Number(b.positionZ) || 0]}>
      {/* NO base pad at all — buildings rise straight out of the existing grass.
          Only LOW ground-level wildflower clusters at the CORNERS (door faces stay
          clear), so nothing is raised and no door is ever hidden. */}
      {(() => {
        const lotW = Math.max(w, 3);
        const lotD = Math.max(d, 3);
        return (
          <group>
            {[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([sx, sz], i) => (
              <FloraCluster
                key={i}
                position={[sx * (lotW / 2 + 0.7), 0, sz * (lotD / 2 + 0.7)]}
                seed={floraSeed * 7 + i * 41}
                night={night}
                count={6}
                spread={0.95}
                scale={1.05}
              />
            ))}
          </group>
        );
      })()}
      {/* hover / active highlight: glowing ground ring (the building no longer resizes) */}
      <mesh ref={ringRef} position={[0, 0.36, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[Math.max(w, d) * 0.92, Math.max(w, d) * 0.92 + 0.28, 56]} />
        <meshBasicMaterial color={BRAND.yellow} transparent opacity={0} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      {/* warm highlight light that brightens the building on hover */}
      <pointLight ref={hoverLightRef} position={[Math.max(d, 3) * 0.2, h * 0.72, Math.max(d, 3) * 0.5]} color={"#FFE9A8"} intensity={0} distance={Math.max(w, d, h) * 3.2} decay={2} />
      {/* floating pin marker pointing at the highlighted building (charcoal-outlined so it reads on any colour) */}
      <group ref={markerRef} position={[0, markerBaseY, 0]} visible={false}>
        <mesh rotation={[Math.PI, 0, 0]} castShadow>
          <coneGeometry args={[0.52, 0.9, 4]} />
          <meshStandardMaterial color={BRAND.charcoal} roughness={0.5} />
        </mesh>
        <mesh rotation={[Math.PI, 0, 0]} position={[0, 0.04, 0]} scale={0.74}>
          <coneGeometry args={[0.52, 0.9, 4]} />
          <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={0.85} metalness={0.2} roughness={0.35} />
        </mesh>
        <mesh position={[0, 0.74, 0]}>
          <sphereGeometry args={[0.13, 12, 12]} />
          <meshBasicMaterial color={BRAND.yellow} />
        </mesh>
      </group>

      {/* Static invisible hit-proxy: captures all pointer events from a NON-moving
          volume so hover/click stay rock-steady even while the building animates. */}
      <mesh
        position={[0, (h + 2.5) / 2, 0]}
        onPointerOver={(e) => { e.stopPropagation(); onHover(b.slug); document.body.style.cursor = "pointer"; }}
        onPointerOut={(e) => { e.stopPropagation(); onHover(null); document.body.style.cursor = "default"; }}
        onClick={(e) => { e.stopPropagation(); onSelect(b.slug); }}
      >
        <boxGeometry args={[Math.max(w, 3) + 1.2, h + 2.5, Math.max(d, 3) + 1.2]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      <group ref={groupRef} rotation={[0, Number(b.rotationY) || 0, 0]}>
        <group ref={visualRef}>
          <ShapeMesh b={b} night={night} />
          <BuildingDetails b={b} />
        </group>
      </group>

      {showLabels && (
        <Html position={[0, h + (b.shape === "beacon" ? 4.6 : 2.6), 0]} center zIndexRange={[18, 0]} style={{ pointerEvents: "none" }}>
          <div
            data-testid={`building-label-${b.slug}`}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              transform: `scale(${isActive || isHovered ? 1.07 : 1})`,
              transition: "transform 160ms cubic-bezier(0.2,0.8,0.2,1)",
              background: isActive ? BRAND.yellow : BRAND.charcoal,
              color: isActive ? BRAND.charcoal : "#fff",
              fontFamily: "'Archivo Narrow', Impact, sans-serif",
              fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
              fontSize: 12, padding: "4px 10px", borderRadius: 7, whiteSpace: "nowrap",
              boxShadow: "0 6px 16px rgba(0,0,0,0.28)",
              border: isActive ? "none" : "1px solid rgba(255,255,255,0.14)",
            }}
          >
            <Icon size={13} strokeWidth={2.4} color={isActive ? BRAND.charcoal : BRAND.yellow} />
            {b.name}
          </div>
        </Html>
      )}
    </group>
  );
}
